/**
 * Chat interno del equipo (dirección, 23/9/2026).
 *
 * «Un chat de dirección, que sería solamente [los administradores], y un chat
 * global, que serían [todos los de administración]… Entras
 * con tu sesión y, cuando escribes un mensaje, sale el mensaje y tu
 * nombrecito. Un chat simple. Y que se quede ahí el historial.»
 *
 * Lo que decide esta pantalla (lo demás lo decide el servidor, en
 * routers/chat_router.py):
 *
 *  · LAS PESTAÑAS SON LAS QUE MANDA EL SERVIDOR. No se calcula aquí quién ve
 *    la de dirección: se pintan las salas de `/api/chat/salas`, y así una
 *    pantalla vieja no puede ofrecer una sala que el servidor ya no da.
 *  · SI LA LECTURA FALLA, SE DICE. «No se ha podido cargar el chat», con
 *    reintentar; nunca una sala vacía, que se leería como «nadie ha escrito».
 *  · SI EL ENVÍO FALLA, EL TEXTO SE QUEDA EN LA CAJA, con el motivo, y el
 *    reintento lleva el MISMO id: el servidor lo reconoce y no lo duplica
 *    aunque el primer intento sí hubiera llegado. Y si falla sin respuesta,
 *    antes de decir «no se ha enviado» se pregunta por ese id: puede que
 *    llegara y se cortara la vuelta (segunda pasada del 23/9/2026).
 *  · CADA SALA TIENE SU BORRADOR, y se guarda en la sesión del navegador:
 *    ni lo de la global se va a dirección al cambiar de pestaña, ni se pierde
 *    al ir a otra pantalla y volver.
 *  · LO NUEVO SE PREGUNTA CADA 5 SEGUNDOS, solo con la pestaña a la vista, y
 *    se pide SOLO lo nuevo (`despues`) más la lista de lo eliminado desde la
 *    vuelta anterior (`borrados_desde`): un mensaje eliminado deja de verse
 *    en todas las pantallas aunque ya no esté entre los últimos.
 *  · EL TEXTO ES TEXTO. React lo escapa; los enlaces http(s) se hacen
 *    pulsables construyéndolos aquí, sin interpretar HTML nunca, y los
 *    caracteres que cambian la dirección del texto no se pintan.
 *  · FOTOS (24/9/2026): hasta 4 por mensaje, por tres caminos —el clip (los
 *    archivos o la galería; SIN abrir la cámara, dirección: «pero no sacar
 *    foto con el móvil»), arrastrarlas sobre el chat y pegarlas en la caja—.
 *    Lo que no vale (tipo o tamaño) se dice ANTES de subir. Se ven antes de
 *    enviar, con su X; al subir, con su barra; y si falla, ni las fotos ni el
 *    texto se pierden y el reintento lleva el mismo id. Pegar desde Excel o
 *    Word pega el TEXTO (traen también una imagen de las celdas): solo se
 *    adjunta lo pegado cuando no trae texto, como una captura. Mientras se
 *    sube, lo que se pega o se suelta NO se añade, y se dice (supervisor,
 *    24/9/2026: antes se añadía y el final de la subida lo borraba sin
 *    avisar).
 *  · ASPECTO DE WHATSAPP (dirección: «¿y que se vean tipo WhatsApp?»): las
 *    burbujas están en components/chat/BurbujaChat.jsx y el visor de fotos
 *    en components/chat/VisorFotos.jsx. Aquí, el fondo, los días y la caja.
 *  · NOTAS DE VOZ (dirección, 28/9/2026: «permite en el chat que puedas
 *    grabar audios. Y que luego el audio tenga una opción de ver
 *    transcripción»). El micrófono de la caja: se pulsa y graba, se ve el
 *    tiempo, y se envía o se descarta; a los 5 minutos para sola y queda
 *    lista para enviar. Grabar es lib/notasVozChat.js (con el formato y los
 *    avisos del micrófono de la grabación de consulta); el reproductor y
 *    «Ver transcripción», components/chat/NotaDeVoz.jsx. Si el envío falla,
 *    la nota se queda (se puede oír antes de reintentar) y el reintento
 *    lleva el MISMO id. Mientras hay una nota a medias no se cambia de sala,
 *    y salir del chat la descarta (como las fotos sin enviar).
 *  · QUIÉN ENTRA: gestión y, desde el 28/9/2026, enfermería, que ve solo la
 *    sala global (las pestañas las manda el servidor).
 *  · PDF Y AUDIOS (dirección, 29/9/2026: «admite poder incorporar pdf y
 *    audio en el chat»). Por los mismos tres caminos que las fotos (el
 *    clip, arrastrar y pegar), y cada archivo se reparte por lo que parece:
 *      - los PDF van en el mensaje con el texto y las fotos (hasta 4, de
 *        10 MB), como una ficha con su X antes de enviar; en la burbuja, con
 *        su nombre, su tamaño, «Abrir» (otra pestaña) y descargar;
 *      - un audio ya grabado (M4A, MP3, WAV, OGG/OPUS, WEBM o AAC) va como
 *        una nota de voz: se queda listo en el sitio de la caja, se puede oír
 *        antes y se envía o se quita, con el mismo reintento. Solo, como las
 *        notas: con fotos o PDF sin enviar, se dice y no se añade;
 *      - un vídeo o cualquier otra cosa, se dice ANTES de subir.
 *    Lo que manda es el servidor, que mira cada archivo por dentro.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { error as logError } from '../lib/logger';
import { useAuth } from '../lib/auth';
import { avisarChatSinLeer, PREFIJO_SESION_CHAT } from '../lib/chatSinLeer';
import { PageHeader } from '../components/Common';
import { Button } from '../components/ui/button';
import {
  Loader2, SendHorizontal, RotateCcw, MessagesSquare, Paperclip, X, Mic, Trash2, FileText, Music,
} from 'lucide-react';
import { toast } from 'sonner';
import BurbujaChat, { sinDireccion } from '../components/chat/BurbujaChat';
import VisorFotos from '../components/chat/VisorFotos';
import { ReproductorNota } from '../components/chat/NotaDeVoz';
import {
  ACEPTA_FOTOS, TOPE_FOTOS, firmaDeFotos, motivoNoValida, pareceUnaFoto,
} from '../lib/fotosChat';
import {
  ACEPTA_PDF, TOPE_MB_PDF, TOPE_PDFS, motivoNoValidoPdf, pareceUnPdf, tamanoLegible,
} from '../lib/pdfsChat';
import {
  ACEPTA_AUDIOS, FORMATOS_AUDIO, GrabadoraNota, MINIMO_MS, TOPE_BYTES_NOTA, TOPE_MB_AUDIO,
  TOPE_SEGUNDOS_AUDIO, TOPE_SEGUNDOS_NOTA, duracionCorta, extensionDe, medirDuracion,
  motivoNoValidoAudio, pareceUnAudio,
} from '../lib/notasVozChat';

const CADA_CUANTO_MS = 5000;
// Cada cuántas vueltas se refrescan también los recuentos de las pestañas
// (6 × 5 s = 30 s, lo mismo que el globo del menú).
const VUELTAS_POR_RECUENTO = 6;
// El mismo tope que el servidor (chat_router.TOPE_TEXTO): se avisa antes de
// mandar, en vez de esperar al 400.
const TOPE_TEXTO = 4000;
const POR_PAGINA = 50;
// En cada vuelta se pide lo nuevo desde el último mensaje, hasta 100. Si hay
// más (la pestaña estuvo oculta un buen rato), se recarga desde el final y lo
// de antes queda en «Ver anteriores»: pedirlo a trozos era más lento y dejaba
// la lista a medias si algo fallaba por el camino.
const POR_VUELTA = 100;

// El borrador y el envío pendiente de cada sala, en la sesión del navegador
// (segunda pasada del 23/9/2026): se perdían al ir a otra pantalla y volver.
// sessionStorage y no localStorage: es de esta pestaña y se va al cerrarla,
// que es lo que se espera de algo a medio escribir. Con el id de quien
// escribe en la clave, para que en un ordenador compartido no le salga a
// otra persona, y se borran todas al cerrar sesión (lib/auth.jsx). Si el
// navegador no deja (modo privado), se sigue sin él.
function claveSesion(tipo, uid, sala) {
  return `${PREFIJO_SESION_CHAT}${tipo}:${uid || '-'}:${sala}`;
}

function leerSesion(tipo, uid, sala) {
  try {
    return window.sessionStorage.getItem(claveSesion(tipo, uid, sala));
  } catch {
    return null;
  }
}

function guardarSesion(tipo, uid, sala, valor) {
  try {
    const clave = claveSesion(tipo, uid, sala);
    if (valor) window.sessionStorage.setItem(clave, valor);
    else window.sessionStorage.removeItem(clave);
  } catch { /* sin almacenamiento: el borrador vive solo en la pantalla */ }
}

/** Caracteres como los cuenta el servidor (Python): un emoji es uno, no los
 *  dos «trozos» que ve `.length`. Si no, la caja avisaría de «demasiado largo»
 *  a un mensaje que el servidor sí acepta. */
function largo(texto) {
  return [...String(texto || '')].length;
}

function nuevoId() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `m${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
}

// Lo que ofrece el clip (29/9/2026): fotos, PDF y audios.
const ACEPTA_ADJUNTOS = [ACEPTA_FOTOS, ACEPTA_PDF, ACEPTA_AUDIOS].join(',');

/** «la foto», «las fotos», «el PDF», «los PDF» o «los archivos»: lo que se
 *  está subiendo, para decirlo en la barra y en los avisos. */
function loQueSeSube(adjuntos) {
  const lista = adjuntos || [];
  const pdfs = lista.filter((a) => a.tipo === 'pdf').length;
  const fotos = lista.length - pdfs;
  if (!pdfs) return fotos === 1 ? 'la foto' : 'las fotos';
  if (!fotos) return pdfs === 1 ? 'el PDF' : 'los PDF';
  return 'los archivos';
}

/** Por qué no se adjunta algo que no es ni foto, ni PDF, ni audio. */
function noSeAdjunta(archivo) {
  const nombre = archivo?.name ? `«${archivo.name}»` : 'Ese archivo';
  const tipo = String(archivo?.type || '').toLowerCase();
  if (tipo.startsWith('video/')) return `${nombre} es un vídeo: en el chat no se pueden enviar vídeos.`;
  // Una imagen de un tipo que no vale (GIF, BMP…): el motivo de las fotos.
  if (tipo.startsWith('image/')) return motivoNoValida(archivo);
  return `${nombre} no se puede adjuntar: en el chat van fotos (JPG, PNG, WEBP o HEIC), `
    + `PDF y audios (${FORMATOS_AUDIO}).`;
}

/** El motivo que da el servidor, si es un texto; si no, el de por defecto. */
function motivo(e, porDefecto) {
  const detalle = e?.response?.data?.detail;
  if (typeof detalle === 'string' && detalle.trim()) return detalle.trim().replace(/\.$/, '');
  if (e && !e.response) return 'No hay conexión con el servidor';
  return porDefecto;
}

// ---------------------------------------------------------------- fechas

function fecha(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function claveDia(iso) {
  const d = fecha(iso);
  return d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : '';
}

/** «Hoy», «Ayer» o «martes 22 de septiembre» (con el año si no es este). */
export function etiquetaDia(iso, ahora = new Date()) {
  const d = fecha(iso);
  if (!d) return '';
  const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  const ese = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // Redondeado: los días del cambio de hora tienen 23 o 25 horas.
  const dias = Math.round((hoy - ese) / 86400000);
  if (dias === 0) return 'Hoy';
  if (dias === 1) return 'Ayer';
  const semana = d.toLocaleDateString('es-ES', { weekday: 'long' });
  const mes = d.toLocaleDateString('es-ES', { month: 'long' });
  const anio = d.getFullYear() !== ahora.getFullYear() ? ` de ${d.getFullYear()}` : '';
  return `${semana} ${d.getDate()} de ${mes}${anio}`;
}

// ---------------------------------------------------------------- mensajes

/** Junta lo que había con lo que llega: por id y en orden de hora. Lo que
 *  llega manda, salvo que deshaga un «eliminado»: eliminar no tiene vuelta
 *  atrás, y una respuesta que salió del servidor un instante antes no puede
 *  volver a enseñar el texto. */
function juntar(actuales, llegan) {
  const porId = new Map(actuales.map((m) => [m.id, m]));
  (llegan || []).forEach((m) => {
    if (!m || !m.id) return;
    const antes = porId.get(m.id);
    let queda = antes?.borrado && !m.borrado ? antes : m;
    // Una transcripción recién pedida no la quita una vuelta que salió del
    // servidor un instante antes de guardarla (28/9/2026).
    if (queda === m && typeof antes?.audio?.transcripcion === 'string'
      && m.audio && typeof m.audio.transcripcion !== 'string') {
      queda = { ...m, audio: { ...m.audio, transcripcion: antes.audio.transcripcion } };
    }
    porId.set(m.id, queda);
  });
  return [...porId.values()].sort((a, b) => {
    const fa = a.creado_at || '';
    const fb = b.creado_at || '';
    if (fa !== fb) return fa < fb ? -1 : 1;
    return a.id < b.id ? -1 : (a.id > b.id ? 1 : 0);
  });
}

/** Los que el servidor dice que se han eliminado, como eliminados. */
function tacharBorrados(lista, borrados) {
  if (!borrados || !borrados.size) return lista;
  return lista.map((m) => (borrados.has(m.id) && !m.borrado
    ? { ...m, borrado: true, texto: '', fotos: [], pdfs: [], audio: null } : m));
}

// La pastilla de los días y de los avisos de la conversación.
export const PASTILLA = 'text-[12.5px] px-3 py-1 rounded-lg bg-[#FFFFFF] dark:bg-[#182229] text-[#54656F] dark:text-[#8696A0] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]';

function Aviso({ testid, titulo, texto, onReintentar }) {
  return (
    <div className="m-4 text-sm text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 rounded-lg p-3"
      data-testid={testid} role="alert">
      <div className="font-medium">{titulo}</div>
      {texto && <div className="mt-0.5">{texto}.</div>}
      {onReintentar && (
        <Button variant="outline" size="sm" className="mt-2" onClick={onReintentar}
          data-testid={`${testid}-reintentar`}>
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reintentar
        </Button>
      )}
    </div>
  );
}

/** «A, B y C», sin caracteres de dirección en ningún nombre (tercera
 *  pasada): igual que el autor de cada mensaje. */
function nombres(lista) {
  const limpios = (lista || []).map(sinDireccion);
  if (!limpios.length) return '';
  if (limpios.length === 1) return limpios[0];
  return `${limpios.slice(0, -1).join(', ')} y ${limpios[limpios.length - 1]}`;
}

// ---------------------------------------------------------------- pantalla

export default function ChatPage() {
  const { user } = useAuth();
  const yo = user?.id;

  const [salas, setSalas] = useState(null);
  const [falloSalas, setFalloSalas] = useState(null);
  const [salaId, setSalaId] = useState(null);

  const [mensajes, setMensajes] = useState([]);
  const [hayMas, setHayMas] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [falloMensajes, setFalloMensajes] = useState(null);
  const [cargandoAnteriores, setCargandoAnteriores] = useState(false);

  // Borrador y fallo de envío POR SALA.
  const [borradores, setBorradores] = useState({});
  const [enviando, setEnviando] = useState(null);
  const [fallosEnvio, setFallosEnvio] = useState({});
  // Las fotos y los PDF elegidos y aún sin enviar, también POR SALA:
  // [{ clave, archivo, url, tipo: 'foto' | 'pdf' }]. No van a la sesión del
  // navegador (un archivo no se puede guardar ahí): se conservan mientras no
  // se salga del chat.
  const [adjuntos, setAdjuntos] = useState({});
  // Lo que no se ha podido adjuntar (tipo o tamaño), dicho antes de subir.
  const [avisosFotos, setAvisosFotos] = useState({});
  const [progreso, setProgreso] = useState(null);     // 0-100 mientras sube
  const [arrastrando, setArrastrando] = useState(false);
  const [visor, setVisor] = useState(null);           // { id, inicio }
  // La nota de voz en curso (una en toda la pantalla: el micrófono es uno):
  // { fase: 'pidiendo' | 'grabando' | 'parando' | 'lista' | 'enviando',
  //   sala, segundos } y, ya grabada, { id, blob, mime, duracionMs, url,
  //   aviso, error }. Un audio elegido (29/9/2026) es una nota ya «lista»
  //   con `nombre` y `archivo: true`.
  const [nota, setNota] = useState(null);
  // Lo que ha impedido grabar (el micrófono, el navegador…), dicho claro.
  const [errorNota, setErrorNota] = useState('');
  // ¿Se puede pedir la transcripción? { disponible, motivo }, de /salas.
  const [transcripcion, setTranscripcion] = useState(null);

  const salaRef = useRef(null);
  const salasRef = useRef(null);
  const mensajesRef = useRef([]);
  const listaRef = useRef(null);
  const scrollRef = useRef(null);     // 'fondo' | { alto, arriba } tras pintar
  const intentosRef = useRef({});     // sala → { id, texto } del envío pendiente
  const consultandoRef = useRef(false);
  // La sala cuya primera carga ha salido bien. Hasta entonces no se pregunta
  // por lo nuevo: con el aviso de «no se ha podido cargar» en pantalla, la
  // vuelta de fondo no puede ir marcando como leído lo que nadie ha visto.
  const listaListaRef = useRef(null);
  // La sala que se ha pintado con la pestaña oculta (abierta en segundo
  // plano): su «leído» se marca al volver a ella, no antes, y no se queda
  // esperando a que alguien escriba otra cosa.
  const pendienteLeerRef = useRef(null);
  // La hora del servidor en la última lectura buena: es el «desde cuándo»
  // de lo eliminado en la vuelta siguiente, y el «desde» de lo nuevo cuando la
  // sala está vacía. La del servidor, no la de este ordenador.
  const ahoraRef = useRef(null);
  const vueltasRef = useRef(0);
  const consultarRef = useRef(() => {});
  const entradaFotosRef = useRef(null);
  const cajaRef = useRef(null);
  // Las vistas previas son direcciones `blob:` del navegador: se sueltan al
  // quitar la foto, al enviarla y al salir del chat.
  const urlsRef = useRef(new Set());
  // Arrastrar sobre los hijos dispara «entrar» y «salir» en cadena: se
  // cuentan para no apagar el aviso de «suelta aquí» a medio camino.
  const arrastreRef = useRef(0);
  // La grabadora de la nota en curso, y si al pararse hay que enviarla (se
  // ha pulsado enviar) o dejarla lista (se ha llegado al tope).
  const grabadoraRef = useRef(null);
  const quiereEnviarRef = useRef(false);
  // Un audio elegido del que el navegador aún está leyendo cuánto dura.
  const preparandoAudioRef = useRef(false);

  /** Cambia la lista y su espejo a la vez: el intervalo lee el espejo. */
  const aplicar = useCallback((fn) => {
    const siguiente = fn(mensajesRef.current);
    mensajesRef.current = siguiente;
    setMensajes(siguiente);
  }, []);

  const cargarSalas = useCallback(async ({ silencioso = false } = {}) => {
    try {
      const r = await api.get('/api/chat/salas', { timeout: 20000 });
      const lista = r.data?.salas;
      if (!Array.isArray(lista)) throw new Error('respuesta sin salas');
      salasRef.current = lista;
      setSalas(lista);
      setFalloSalas(null);
      avisarChatSinLeer(Number(r.data?.no_leidos_total));
      setTranscripcion(r.data?.transcripcion || null);
      setSalaId((actual) => (actual && lista.some((s) => s.id === actual) ? actual : (lista[0]?.id || null)));
    } catch (e) {
      logError('chat: salas', e);
      // Un refresco que falla no tapa lo que ya se ve: solo la primera carga
      // se queda en «no se ha podido cargar».
      if (!silencioso || !salasRef.current) {
        setFalloSalas({
          texto: motivo(e, 'El servidor no ha contestado'),
          sinPermiso: e?.response?.status === 403,
        });
      }
    }
  }, []);

  const marcarLeido = useCallback(async (sala, hastaId) => {
    if (!sala || !hastaId) return;
    if (document.visibilityState === 'hidden') {
      pendienteLeerRef.current = sala;
      return;
    }
    if (pendienteLeerRef.current === sala) pendienteLeerRef.current = null;
    try {
      const r = await api.post(`/api/chat/salas/${sala}/leido`, { hasta: hastaId });
      const pendientes = Number(r.data?.no_leidos) || 0;
      setSalas((prev) => (prev ? prev.map((s) => (s.id === sala ? { ...s, no_leidos: pendientes } : s)) : prev));
      avisarChatSinLeer();
    } catch (e) {
      // No pasa nada si falla: en la próxima vuelta se vuelve a marcar.
      logError('chat: leído', e);
    }
  }, []);

  /** La última página de la sala. `silencioso`: sin «Cargando…» ni aviso de
   *  fallo, para recargar por detrás lo que ya se está viendo. */
  const cargarMensajes = useCallback(async (sala, { silencioso = false } = {}) => {
    if (!silencioso) {
      listaListaRef.current = null;
      setCargando(true);
      setFalloMensajes(null);
    }
    try {
      const r = await api.get(`/api/chat/salas/${sala}/mensajes`,
        { params: { limite: POR_PAGINA }, timeout: 20000 });
      if (salaRef.current !== sala) return;
      const lista = r.data?.mensajes;
      if (!Array.isArray(lista)) throw new Error('respuesta sin mensajes');
      scrollRef.current = 'fondo';
      aplicar(() => juntar([], lista));
      setHayMas(Boolean(r.data?.hay_mas));
      ahoraRef.current = r.data?.ahora || null;
      listaListaRef.current = sala;
      if (lista.length) marcarLeido(sala, lista[lista.length - 1].id);
    } catch (e) {
      if (salaRef.current !== sala) return;
      logError('chat: mensajes', e);
      // Por detrás, lo que se ve se queda y se vuelve a intentar en la
      // siguiente vuelta.
      if (silencioso) return;
      aplicar(() => []);
      setFalloMensajes(motivo(e, 'El servidor no ha contestado'));
    } finally {
      if (!silencioso && salaRef.current === sala) setCargando(false);
    }
  }, [aplicar, marcarLeido]);

  /** Lo nuevo de la sala abierta. Si falla, lo que hay se queda y se vuelve a
   *  preguntar en la siguiente vuelta. */
  const consultarNuevos = useCallback(async () => {
    const sala = salaRef.current;
    if (!sala || listaListaRef.current !== sala || consultandoRef.current
      || document.visibilityState === 'hidden') return;
    consultandoRef.current = true;
    vueltasRef.current += 1;
    if (vueltasRef.current % VUELTAS_POR_RECUENTO === 0) cargarSalas({ silencioso: true });
    try {
      const actuales = mensajesRef.current;
      const ultimo = actuales[actuales.length - 1];
      // Solo lo nuevo: desde el último que se tiene o, con la sala vacía,
      // desde la hora de la última lectura. Y lo eliminado desde entonces.
      const params = { limite: POR_VUELTA };
      const desde = ultimo?.id || ahoraRef.current;
      if (desde) params.despues = desde;
      if (ahoraRef.current) params.borrados_desde = ahoraRef.current;
      const r = await api.get(`/api/chat/salas/${sala}/mensajes`, { params, timeout: 15000 });
      if (salaRef.current !== sala) return;
      if (r.data?.hay_mas) {
        // Más de 100 nuevos desde la última vuelta: se recarga desde el final,
        // y lo de antes queda detrás de «Ver anteriores» (que sale solo,
        // porque el servidor dice que hay más).
        await cargarMensajes(sala, { silencioso: true });
        return;
      }
      const llegan = Array.isArray(r.data?.mensajes) ? r.data.mensajes : [];
      const borrados = new Set(Array.isArray(r.data?.borrados) ? r.data.borrados : []);
      const conocidos = new Map(actuales.map((m) => [m.id, m]));
      const nuevos = llegan.filter((m) => !conocidos.has(m.id));
      const deOtros = nuevos.filter((m) => m.autor_id !== yo);
      const cambia = nuevos.length > 0
        || llegan.some((m) => conocidos.has(m.id) && m.borrado && !conocidos.get(m.id).borrado)
        || actuales.some((m) => borrados.has(m.id) && !m.borrado);
      if (cambia) {
        const el = listaRef.current;
        const alFondo = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 120;
        if (alFondo && nuevos.length) scrollRef.current = 'fondo';
        aplicar((prev) => tacharBorrados(juntar(prev, llegan), borrados));
      }
      if (r.data?.ahora) ahoraRef.current = r.data.ahora;
      // Se marca leído cuando llega algo de otros, o al volver a una pestaña
      // que se había pintado oculta. Lo propio no hace falta marcarlo: no
      // cuenta como sin leer.
      if (deOtros.length || pendienteLeerRef.current === sala) {
        const ultimo = mensajesRef.current[mensajesRef.current.length - 1];
        if (ultimo) marcarLeido(sala, ultimo.id);
      }
    } catch (e) {
      logError('chat: nuevos', e);
      // Si el servidor no entiende la pregunta (un 400: la referencia ya no
      // le sirve), repetirla cada 5 s no lleva a nada y el chat se quedaría
      // quieto sin decirlo. Se recarga desde el final, por detrás.
      if (e?.response?.status === 400 && salaRef.current === sala) {
        await cargarMensajes(sala, { silencioso: true });
      }
    } finally {
      consultandoRef.current = false;
    }
  }, [aplicar, cargarMensajes, cargarSalas, marcarLeido, yo]);

  useEffect(() => { consultarRef.current = consultarNuevos; }, [consultarNuevos]);

  useEffect(() => { cargarSalas(); }, [cargarSalas]);

  // Al cambiar de sala: lista nueva, desde el final.
  useEffect(() => {
    salaRef.current = salaId;
    if (!salaId) return;
    aplicar(() => []);
    setHayMas(false);
    ahoraRef.current = null;
    cargarMensajes(salaId);
  }, [salaId, aplicar, cargarMensajes]);

  // Cada 5 s, solo con la pestaña a la vista; al volver a ella, en el acto.
  useEffect(() => {
    if (!salaId) return undefined;
    let id = null;
    const arrancar = () => {
      if (id == null) id = setInterval(() => consultarRef.current(), CADA_CUANTO_MS);
    };
    const parar = () => {
      if (id != null) { clearInterval(id); id = null; }
    };
    const alCambiar = () => {
      if (document.visibilityState === 'visible') {
        consultarRef.current();
        arrancar();
      } else {
        parar();
      }
    };
    if (document.visibilityState !== 'hidden') arrancar();
    document.addEventListener('visibilitychange', alCambiar);
    return () => {
      parar();
      document.removeEventListener('visibilitychange', alCambiar);
    };
  }, [salaId]);

  useEffect(() => () => {
    urlsRef.current.forEach((u) => {
      try { URL.revokeObjectURL(u); } catch { /* ya suelta */ }
    });
    urlsRef.current.clear();
  }, []);

  // Salir del chat con una nota a medias: se para el micrófono y se tira.
  useEffect(() => () => {
    const g = grabadoraRef.current;
    grabadoraRef.current = null;
    if (g) g.descartar();
  }, []);

  // Después de pintar: al fondo (al abrir, al llegar algo estando abajo, al
  // enviar) o donde se estaba (al cargar los anteriores por arriba).
  useLayoutEffect(() => {
    const el = listaRef.current;
    const quiero = scrollRef.current;
    scrollRef.current = null;
    if (!el || !quiero) return;
    if (quiero === 'fondo') el.scrollTop = el.scrollHeight;
    else el.scrollTop = el.scrollHeight - quiero.alto + quiero.arriba;
  }, [mensajes]);

  const verAnteriores = async () => {
    const sala = salaRef.current;
    const primero = mensajesRef.current[0];
    if (!sala || !primero || cargandoAnteriores) return;
    setCargandoAnteriores(true);
    try {
      const r = await api.get(`/api/chat/salas/${sala}/mensajes`,
        { params: { antes: primero.id, limite: POR_PAGINA }, timeout: 20000 });
      if (salaRef.current !== sala) return;
      const el = listaRef.current;
      scrollRef.current = el ? { alto: el.scrollHeight, arriba: el.scrollTop } : null;
      aplicar((prev) => juntar(prev, r.data?.mensajes || []));
      setHayMas(Boolean(r.data?.hay_mas));
    } catch (e) {
      toast.error(`No se han podido cargar los mensajes anteriores: ${motivo(e, 'el servidor no ha contestado')}`);
    } finally {
      setCargandoAnteriores(false);
    }
  };

  /** El borrador de la sala: lo que hay en pantalla o, si aún no se ha
   *  tocado en esta visita, lo que se guardó en la sesión. */
  const borradorDe = (sala) => {
    if (!sala) return '';
    if (Object.prototype.hasOwnProperty.call(borradores, sala)) return borradores[sala];
    return leerSesion('borrador', yo, sala) || '';
  };
  const ponerBorrador = (sala, valor) => {
    setBorradores((prev) => ({ ...prev, [sala]: valor }));
    guardarSesion('borrador', yo, sala, valor);
  };
  /** El envío pendiente de la sala ({ id, texto }), que también sobrevive a
   *  salir y volver: el reintento de después sigue llevando el mismo id. */
  const intentoDe = (sala) => {
    if (intentosRef.current[sala]) return intentosRef.current[sala];
    try {
      return JSON.parse(leerSesion('intento', yo, sala) || 'null');
    } catch {
      return null;
    }
  };
  const ponerIntento = (sala, intento) => {
    if (intento) intentosRef.current[sala] = intento;
    else delete intentosRef.current[sala];
    guardarSesion('intento', yo, sala, intento ? JSON.stringify(intento) : null);
  };

  const texto = borradorDe(salaId);
  const setTexto = (valor) => ponerBorrador(salaId, valor);
  const falloEnvio = salaId ? fallosEnvio[salaId] : null;
  const adjuntosSala = (salaId && adjuntos[salaId]) || [];
  const fotosSala = adjuntosSala.filter((a) => a.tipo !== 'pdf');
  const pdfsSala = adjuntosSala.filter((a) => a.tipo === 'pdf');
  const avisosSala = (salaId && avisosFotos[salaId]) || [];

  // La caja crece con lo que se escribe, hasta unas seis líneas.
  useLayoutEffect(() => {
    const el = cajaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight || 0, 160)}px`;
  }, [texto, salaId]);

  const soltarUrl = (url) => {
    if (!url) return;
    try { URL.revokeObjectURL(url); } catch { /* ya suelta */ }
    urlsRef.current.delete(url);
  };

  /** Un aviso más en la sala, sin repetirlo. */
  const avisarEnSala = (sala, aviso) => {
    setAvisosFotos((prev) => {
      const ya = prev[sala] || [];
      return ya.includes(aviso) ? prev : { ...prev, [sala]: [...ya, aviso] };
    });
  };

  /** Un audio elegido (29/9/2026): se lee cuánto dura y queda como una nota
   *  de voz lista para enviar (con su vista previa), o se dice por qué no. */
  const prepararAudio = async (sala, archivo) => {
    preparandoAudioRef.current = true;
    setErrorNota('');
    let duracionMs = null;
    try {
      duracionMs = await medirDuracion(archivo);
    } finally {
      preparandoAudioRef.current = false;
    }
    if (salaRef.current !== sala) return;
    const nombre = archivo.name || 'audio';
    if (duracionMs && duracionMs > TOPE_SEGUNDOS_AUDIO * 1000) {
      avisarEnSala(sala, `«${nombre}» dura ${duracionCorta(duracionMs / 1000)} y el máximo de un audio `
        + `son ${TOPE_SEGUNDOS_AUDIO / 60} minutos.`);
      return;
    }
    let url = '';
    try {
      url = URL.createObjectURL(archivo);
      urlsRef.current.add(url);
    } catch { /* sin vista previa: se puede enviar igual */ }
    // Mientras se leía la duración no se ha podido empezar a grabar ni
    // añadir otro audio (`preparandoAudioRef`): la caja es suya.
    setNota({
      fase: 'lista', sala, segundos: Math.round((duracionMs || 0) / 1000), id: nuevoId(),
      blob: archivo, mime: archivo.type || '', duracionMs: duracionMs || 0, url,
      aviso: '', error: '', nombre, archivo: true,
    });
  };

  /** Añade fotos, PDF o un audio a la sala abierta (el clip, arrastrar o
   *  pegar), cada uno por lo que parece. Lo que no vale se dice aquí, antes
   *  de subir nada, con su motivo. Mientras se suben los de esta sala no se
   *  añade nada (el clip ya está apagado; pegar y soltar pasan por aquí): se
   *  dice cuáles no se han añadido. */
  const anadirArchivos = (lista) => {
    const sala = salaRef.current;
    const archivos = Array.from(lista || []).filter(Boolean);
    if (!sala || !archivos.length) return;
    if (enviando === sala) {
      const cuales = archivos.map((a) => `«${a.name || 'la imagen'}»`).join(', ');
      const una = archivos.length === 1;
      const aviso = `Se están subiendo ${loQueSeSube(adjuntos[sala])}: ${cuales} no se ${una ? 'ha añadido' : 'han añadido'}. `
        + `Cuando termine la subida, vuelve a ${una ? 'pegarla o arrastrarla' : 'pegarlas o arrastrarlas'}.`;
      avisarEnSala(sala, aviso);
      return;
    }
    const ya = adjuntos[sala] || [];
    let fotosYa = ya.filter((a) => a.tipo !== 'pdf').length;
    let pdfsYa = ya.length - fotosYa;
    const avisos = [];
    const nuevos = [];
    const audios = [];
    archivos.forEach((archivo) => {
      const nombre = `«${archivo.name || 'el archivo'}»`;
      if (pareceUnaFoto(archivo)) {
        const mal = motivoNoValida(archivo);
        if (mal) {
          avisos.push(mal);
          return;
        }
        if (fotosYa >= TOPE_FOTOS) {
          avisos.push(`Como mucho ${TOPE_FOTOS} fotos por mensaje: «${archivo.name || 'la imagen'}» no se ha añadido.`);
          return;
        }
        fotosYa += 1;
        let url = '';
        try {
          url = URL.createObjectURL(archivo);
          urlsRef.current.add(url);
        } catch { /* sin vista previa: se enseña el nombre */ }
        nuevos.push({ clave: nuevoId(), archivo, url, tipo: 'foto' });
      } else if (pareceUnPdf(archivo)) {
        const mal = motivoNoValidoPdf(archivo);
        if (mal) {
          avisos.push(mal);
          return;
        }
        if (pdfsYa >= TOPE_PDFS) {
          avisos.push(`Como mucho ${TOPE_PDFS} PDF por mensaje: ${nombre} no se ha añadido.`);
          return;
        }
        pdfsYa += 1;
        nuevos.push({ clave: nuevoId(), archivo, url: '', tipo: 'pdf' });
      } else if (pareceUnAudio(archivo)) {
        audios.push(archivo);
      } else {
        avisos.push(noSeAdjunta(archivo));
      }
    });
    // El audio va solo, como una nota de voz: uno, y sin fotos ni PDF
    // esperando (se quedarían detrás sin saber cuál va primero).
    const [audio, ...otros] = audios;
    otros.forEach((a) => avisos.push(`Los audios van de uno en uno: «${a.name || 'el audio'}» no se ha añadido.`));
    let preparar = null;
    if (audio) {
      const nombre = `«${audio.name || 'el audio'}»`;
      const mal = motivoNoValidoAudio(audio);
      if (mal) avisos.push(mal);
      else if (ya.length + nuevos.length) {
        avisos.push(`Un audio va solo en su mensaje: envía o quita antes las fotos y los PDF, y vuelve a añadir ${nombre}.`);
      } else if (nota || grabadoraRef.current || preparandoAudioRef.current) {
        avisos.push(`Ya hay una nota de voz a medias: envíala o descártala y vuelve a añadir ${nombre}.`);
      } else {
        preparar = audio;
      }
    }
    setAvisosFotos((prev) => ({ ...prev, [sala]: avisos }));
    if (nuevos.length) setAdjuntos((prev) => ({ ...prev, [sala]: [...(prev[sala] || []), ...nuevos] }));
    if (preparar) prepararAudio(sala, preparar);
  };

  const quitarFoto = (clave) => {
    const sala = salaRef.current;
    const quitada = (adjuntos[sala] || []).find((f) => f.clave === clave);
    soltarUrl(quitada?.url);
    setAdjuntos((prev) => ({ ...prev, [sala]: (prev[sala] || []).filter((f) => f.clave !== clave) }));
    setAvisosFotos((prev) => ({ ...prev, [sala]: [] }));
  };

  /** Quita las fotos YA ENVIADAS (y suelta sus `blob:`), no todas: lo que
   *  hubiera llegado mientras tanto no desaparece sin avisar. Los avisos se
   *  limpian solo si son los de antes del envío. */
  const quitarEnviadas = (sala, enviadas, avisosDeAntes) => {
    const claves = new Set(enviadas.map((f) => f.clave));
    enviadas.forEach((f) => soltarUrl(f.url));
    setAdjuntos((prev) => ({ ...prev, [sala]: (prev[sala] || []).filter((f) => !claves.has(f.clave)) }));
    setAvisosFotos((prev) => (prev[sala] === avisosDeAntes ? { ...prev, [sala]: [] } : prev));
  };

  const alElegirFotos = (e) => {
    anadirArchivos(e.target.files);
    // Para poder volver a elegir la misma foto después de quitarla.
    e.target.value = '';
  };

  /** Pegar con Cmd+V / Ctrl+V. Si el portapapeles trae TEXTO, se pega el
   *  texto y no se adjunta nada: Excel y Word ponen, junto al texto, una
   *  imagen de lo copiado, y antes se adjuntaba la imagen y el texto no se
   *  pegaba (supervisor, 24/9/2026). Solo se adjunta cuando no trae texto:
   *  una captura de pantalla o «copiar imagen» del navegador.
   *  Con una excepción: Finder, al copiar una foto, pone como texto solo
   *  su NOMBRE. Si el texto no es más que los nombres de los archivos, se
   *  adjuntan (segundo supervisor, 24/9/2026). */
  const alPegar = (e) => {
    const datos = e.clipboardData;
    if (!datos) return;
    let textoPegado = '';
    try {
      textoPegado = typeof datos.getData === 'function' ? (datos.getData('text/plain') || '') : '';
    } catch { /* sin acceso al texto: se mira solo si hay imagen */ }
    let archivos = Array.from(datos.items || [])
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter(Boolean);
    if (!archivos.length && datos.files?.length) archivos = Array.from(datos.files);
    if (!archivos.length) return;
    if (/\S/.test(textoPegado)) {
      const lineas = textoPegado.split(/[\r\n]+/).map((l) => l.trim()).filter(Boolean);
      const nombres = new Set(archivos.map((f) => (f.name || '').trim()));
      if (!lineas.every((l) => nombres.has(l))) return;
    }
    e.preventDefault();
    anadirArchivos(archivos);
  };

  const traeArchivos = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const alEntrarArrastrando = (e) => {
    if (!traeArchivos(e)) return;
    e.preventDefault();
    arrastreRef.current += 1;
    setArrastrando(true);
  };
  const alArrastrarEncima = (e) => {
    if (!traeArchivos(e)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };
  const alSalirArrastrando = (e) => {
    if (!traeArchivos(e)) return;
    arrastreRef.current = Math.max(0, arrastreRef.current - 1);
    if (!arrastreRef.current) setArrastrando(false);
  };
  const alSoltar = (e) => {
    if (!traeArchivos(e)) return;
    // Siempre: sin esto el navegador abre la foto soltada en la pestaña.
    e.preventDefault();
    arrastreRef.current = 0;
    setArrastrando(false);
    anadirArchivos(e.dataTransfer.files);
  };

  /** ¿Llegó al servidor el mensaje con este id? Para cuando el envío se
   *  queda sin respuesta: puede haberse guardado y cortarse la vuelta. */
  const comprobarSiLlego = async (sala, id) => {
    try {
      const r = await api.get(`/api/chat/salas/${sala}/mensajes/${id}`, { timeout: 10000 });
      const m = r.data?.mensaje;
      return m && m.id === id ? m : null;
    } catch {
      return null;
    }
  };

  const enviar = async () => {
    const sala = salaRef.current;
    const escrito = borradorDe(sala);
    const limpio = escrito.replace(/\r\n?/g, '\n').trim();
    // Las fotos y los PDF, en el orden en que se añadieron (29/9/2026).
    const adjuntosEnvio = (sala && adjuntos[sala]) || [];
    const fotos = adjuntosEnvio.filter((a) => a.tipo !== 'pdf');
    const pdfs = adjuntosEnvio.filter((a) => a.tipo === 'pdf');
    if (!sala || enviando || (!limpio && !adjuntosEnvio.length)) return;
    if (largo(limpio) > TOPE_TEXTO) {
      setFallosEnvio((prev) => ({
        ...prev,
        [sala]: `El mensaje es demasiado largo: ${largo(limpio)} caracteres, y el máximo son ${TOPE_TEXTO}. Pártelo en dos mensajes`,
      }));
      return;
    }
    // El reintento del MISMO texto (y las mismas fotos y PDF) lleva el MISMO
    // id: si el primer intento sí llegó al servidor, este lo reconoce y no lo
    // duplica. Si se ha cambiado algo, es otro mensaje.
    const antes = intentoDe(sala);
    const firma = firmaDeFotos(adjuntosEnvio.map((f) => f.archivo));
    const id = antes && antes.texto === escrito && (antes.fotos || '') === firma ? antes.id : nuevoId();
    ponerIntento(sala, { id, texto: escrito, fotos: firma });
    const avisosDeAntes = avisosFotos[sala];
    setEnviando(sala);
    setFallosEnvio((prev) => ({ ...prev, [sala]: null }));
    let enviado = null;
    let fallo = null;
    try {
      if (adjuntosEnvio.length) {
        const datos = new FormData();
        fotos.forEach((f) => datos.append('fotos', f.archivo, f.archivo.name || 'foto.jpg'));
        pdfs.forEach((f) => datos.append('pdfs', f.archivo, f.archivo.name || 'documento.pdf'));
        datos.append('id', id);
        if (limpio) datos.append('texto', escrito);
        setProgreso(0);
        // Solo fotos, por su ruta de siempre; con algún PDF, por la de los
        // adjuntos (el mismo camino en el servidor).
        const ruta = pdfs.length ? 'adjuntos' : 'fotos';
        const r = await api.post(`/api/chat/salas/${sala}/${ruta}`, datos, {
          // Cuatro fotos y cuatro PDF de 10 MB por una conexión de móvil tardan.
          timeout: pdfs.length ? 300000 : 180000,
          onUploadProgress: (ev) => {
            if (ev?.total) setProgreso(Math.min(100, Math.round((ev.loaded / ev.total) * 100)));
          },
        });
        enviado = r.data?.mensaje || {};
      } else {
        const r = await api.post(`/api/chat/salas/${sala}/mensajes`, { id, texto: escrito }, { timeout: 20000 });
        enviado = r.data?.mensaje || {};
      }
    } catch (e) {
      logError('chat: enviar', e);
      // Sin respuesta, o un fallo del propio servidor, no quiere decir que
      // no haya llegado (segunda pasada del 23/9/2026): salía el mensaje en
      // la sala y a la vez «no se ha enviado». Se pregunta por su id antes
      // de decirlo. Un 400, 403 o 429 sí es un «no» seguro.
      const estado = e?.response?.status;
      if (!estado || estado >= 500) enviado = await comprobarSiLlego(sala, id);
      if (!enviado) fallo = motivo(e, 'El servidor no ha contestado');
    }
    setProgreso(null);
    if (fallo) {
      // El texto y las fotos se quedan donde estaban, listos para reintentar.
      setFallosEnvio((prev) => ({ ...prev, [sala]: fallo }));
    } else {
      ponerIntento(sala, null);
      ponerBorrador(sala, '');
      quitarEnviadas(sala, adjuntosEnvio, avisosDeAntes);
      if (enviado?.id && salaRef.current === sala) {
        scrollRef.current = 'fondo';
        aplicar((prev) => juntar(prev, [enviado]));
      }
    }
    setEnviando(null);
  };

  // ------------------------------------------------------------ notas de voz

  /** Sube una nota ya grabada. Con el MISMO id en cada reintento: si el
   *  primero sí llegó, el servidor lo reconoce y no la duplica. Si falla,
   *  la nota se queda (se puede oír y reintentar o descartar). */
  const subirNota = async (lista) => {
    const { sala } = lista;
    setNota({ ...lista, fase: 'enviando', error: '' });
    let enviado = null;
    let fallo = null;
    try {
      const datos = new FormData();
      if (lista.archivo) {
        // Un audio elegido (29/9/2026): con su nombre, y la duración solo si
        // el navegador la ha sabido leer.
        datos.append('audio', lista.blob, lista.nombre || 'audio');
        datos.append('origen', 'archivo');
        if (lista.duracionMs > 0) datos.append('duracion_ms', String(Math.round(lista.duracionMs)));
      } else {
        datos.append('audio', lista.blob, `nota-de-voz.${extensionDe(lista.mime)}`);
        datos.append('duracion_ms', String(Math.max(0, Math.round(lista.duracionMs || 0))));
      }
      datos.append('id', lista.id);
      // Un audio de 10 MB por una conexión de móvil tarda.
      const r = await api.post(`/api/chat/salas/${sala}/audios`, datos, { timeout: lista.archivo ? 300000 : 120000 });
      enviado = r.data?.mensaje || {};
    } catch (e) {
      logError('chat: nota de voz', e);
      // Como con el texto: sin respuesta (o un fallo del servidor) no quiere
      // decir que no haya llegado. Se pregunta por su id.
      const estado = e?.response?.status;
      if (!estado || estado >= 500) enviado = await comprobarSiLlego(sala, lista.id);
      if (!enviado) fallo = motivo(e, 'El servidor no ha contestado');
    }
    if (fallo) {
      setNota({ ...lista, fase: 'lista', error: fallo });
      return;
    }
    soltarUrl(lista.url);
    setNota(null);
    if (enviado?.id && salaRef.current === sala) {
      scrollRef.current = 'fondo';
      aplicar((prev) => juntar(prev, [enviado]));
    }
  };

  /** La grabadora ha parado (se ha pulsado enviar, se ha llegado al tope o
   *  el navegador la ha cortado) y entrega lo grabado. */
  const alPararNota = (g, grabada) => {
    if (grabadoraRef.current !== g) return;   // descartada entre medias
    grabadoraRef.current = null;
    const enviarYa = quiereEnviarRef.current;
    quiereEnviarRef.current = false;
    if (!grabada) {
      setNota(null);
      setErrorNota('No se ha grabado sonido: revisa que el micrófono funcione y vuelve a intentarlo.');
      return;
    }
    if ((grabada.duracionMs || 0) < MINIMO_MS) {
      setNota(null);
      setErrorNota('La nota de voz era demasiado corta y no se ha enviado. Pulsa el micrófono, habla y después pulsa enviar.');
      return;
    }
    if (grabada.blob.size > TOPE_BYTES_NOTA) {
      setNota(null);
      setErrorNota(`La nota de voz pesa más de ${TOPE_BYTES_NOTA / (1024 * 1024)} MB y no se puede enviar. Grábala más corta.`);
      return;
    }
    let url = '';
    try {
      url = URL.createObjectURL(grabada.blob);
      urlsRef.current.add(url);
    } catch { /* sin vista previa: se puede enviar igual */ }
    const lista = {
      fase: 'lista', sala: g.sala, segundos: Math.round(grabada.duracionMs / 1000), id: nuevoId(),
      blob: grabada.blob, mime: grabada.mime, duracionMs: grabada.duracionMs, url,
      aviso: grabada.aviso || '', error: '',
    };
    if (enviarYa) subirNota(lista);
    else setNota(lista);
  };

  /** El micrófono de la caja: pide permiso y empieza a grabar. */
  const empezarNota = async () => {
    const sala = salaRef.current;
    if (!sala || nota || grabadoraRef.current || preparandoAudioRef.current || enviando === sala) return;
    setErrorNota('');
    const g = new GrabadoraNota({
      alCambiar: (e) => {
        if (grabadoraRef.current !== g || e.fase !== 'grabando') return;
        setNota((n) => (n && ['pidiendo', 'grabando'].includes(n.fase)
          ? { ...n, fase: 'grabando', segundos: e.segundos } : n));
      },
      alParar: (grabada) => alPararNota(g, grabada),
    });
    g.sala = sala;
    grabadoraRef.current = g;
    quiereEnviarRef.current = false;
    setNota({ fase: 'pidiendo', sala, segundos: 0 });
    const ok = await g.empezar();
    if (!ok && grabadoraRef.current === g) {
      grabadoraRef.current = null;
      setNota(null);
      if (g.estado.error) setErrorNota(g.estado.error);
    }
  };

  const enviarNota = () => {
    if (!nota) return;
    if (nota.fase === 'grabando') {
      quiereEnviarRef.current = true;
      setNota((n) => (n ? { ...n, fase: 'parando' } : n));
      if (grabadoraRef.current) grabadoraRef.current.parar();
      return;
    }
    if (nota.fase === 'lista') subirNota(nota);
  };

  const descartarNota = () => {
    const g = grabadoraRef.current;
    grabadoraRef.current = null;
    quiereEnviarRef.current = false;
    if (g) g.descartar();
    if (nota?.url) soltarUrl(nota.url);
    setNota(null);
    setErrorNota('');
  };

  /** «Ver transcripción» la ha traído: se queda en el mensaje. */
  const alTranscribir = (m, texto) => {
    aplicar((prev) => prev.map((x) => (x.id === m.id && x.audio
      ? { ...x, audio: { ...x.audio, transcripcion: texto } } : x)));
  };

  const alTeclear = (e) => {
    // Enter envía; Mayúsculas+Enter es un salto de línea. Mientras se compone
    // una letra con acento (teclados con tecla muerta, japonés…), Enter no
    // es enviar.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      enviar();
    }
  };

  const borrar = async (m) => {
    if (!window.confirm('¿Eliminar este mensaje? No se puede deshacer')) return;
    try {
      const r = await api.delete(`/api/chat/salas/${m.sala || salaRef.current}/mensajes/${m.id}`);
      const nuevo = r.data?.mensaje;
      aplicar((prev) => prev.map((x) => (x.id === m.id
        ? (nuevo || { ...x, borrado: true, texto: '', fotos: [], pdfs: [], audio: null }) : x)));
    } catch (e) {
      toast.error(`No se ha podido eliminar el mensaje: ${motivo(e, 'el servidor no ha contestado')}`);
    }
  };

  const sala = salas?.find((s) => s.id === salaId) || null;
  const puedeBorrar = Boolean(sala?.puedo_borrar_mios);
  // El visor enseña las fotos del mensaje TAL Y COMO ESTÁ AHORA: si alguien
  // lo elimina con el visor abierto, se cierra solo.
  const mensajeVisor = visor
    ? mensajes.find((x) => x.id === visor.id && !x.borrado && x.fotos?.length) : null;
  const puedeEnviar = !enviando && (texto.trim() || adjuntosSala.length);

  // ---------------------------------------------------------------- render

  let cuerpo;
  if (falloSalas && !salas) {
    cuerpo = (
      <Aviso testid="chat-fallo" titulo="No se ha podido cargar el chat"
        texto={falloSalas.texto}
        onReintentar={falloSalas.sinPermiso ? null : () => cargarSalas()} />
    );
  } else if (!salas) {
    cuerpo = (
      <div className="flex items-center gap-2 text-muted-foreground text-sm p-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando el chat…
      </div>
    );
  } else if (!salas.length) {
    cuerpo = <Aviso testid="chat-sin-salas" titulo="No tienes ninguna sala de chat" />;
  } else {
    let diaAnterior = null;
    let anterior = null;
    cuerpo = (
      <>
        <div className="flex items-end gap-1 border-b border-border px-2 sm:px-3 pt-2 overflow-x-auto" role="tablist">
          {salas.map((s) => {
            const activa = s.id === salaId;
            return (
              <button key={s.id} type="button" role="tab" aria-selected={activa}
                /* Con una nota de voz a medias no se cambia de sala: es de
                   esta, y el micrófono sigue abierto. */
                disabled={Boolean(nota) && !activa}
                title={nota && !activa ? 'Envía o descarta la nota de voz antes de cambiar de sala' : undefined}
                onClick={() => {
                  if (activa || nota) return;
                  setSalaId(s.id);
                  // De paso, los recuentos de las pestañas al día.
                  cargarSalas({ silencioso: true });
                }}
                data-testid={`chat-pestana-${s.id}`}
                className={`relative -mb-px inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-sm rounded-t-lg border transition-colors whitespace-nowrap ${
                  activa
                    ? 'border-border border-b-transparent bg-white dark:bg-card font-semibold text-[#13231E]'
                    : 'border-transparent text-muted-foreground hover:text-[#13231E] disabled:opacity-50 disabled:hover:text-muted-foreground'
                }`}>
                {s.nombre}
                {!activa && s.no_leidos > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold"
                    data-testid={`chat-pestana-${s.id}-sin-leer`}>
                    {s.no_leidos > 99 ? '99+' : s.no_leidos}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {sala?.miembros?.length ? (
          <div className="px-3 sm:px-4 py-1.5 text-[11px] text-muted-foreground border-b border-border truncate"
            data-testid="chat-miembros" title={nombres(sala.miembros)}>
            Lo leen: {nombres(sala.miembros)}
          </div>
        ) : null}

        {/* El fondo de la conversación, como en WhatsApp: suave y distinto
            del de las burbujas. */}
        <div ref={listaRef} className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-[5%] py-3 bg-[#EFEAE2] dark:bg-[#0B141A]"
          data-testid="chat-lista" aria-live="polite">
          {falloMensajes ? (
            <Aviso testid="chat-fallo" titulo="No se ha podido cargar el chat"
              texto={`${falloMensajes}. Los mensajes siguen guardados: es que no se han podido leer ahora`}
              onReintentar={() => cargarMensajes(salaId)} />
          ) : cargando ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando mensajes…
            </div>
          ) : (
            <>
              {hayMas && (
                <div className="flex justify-center pb-1">
                  <button type="button" onClick={verAnteriores} disabled={cargandoAnteriores}
                    className={`${PASTILLA} inline-flex items-center gap-1.5 hover:brightness-95 disabled:opacity-60`}
                    data-testid="chat-ver-anteriores">
                    {cargandoAnteriores && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Ver anteriores
                  </button>
                </div>
              )}
              {!mensajes.length && (
                <div className="flex justify-center py-10" data-testid="chat-vacio">
                  <div className={`${PASTILLA} max-w-xs text-center py-3 px-4`}>
                    <MessagesSquare className="w-7 h-7 mx-auto mb-1.5 text-[#2E6E5E] dark:text-[#06CF9C]" />
                    Todavía no hay mensajes en esta sala. Escribe el primero.
                  </div>
                </div>
              )}
              {mensajes.map((m) => {
                const dia = claveDia(m.creado_at);
                const nuevoDia = dia !== diaAnterior;
                // La racha: mensajes seguidos de la misma persona, el mismo
                // día. Solo el primero lleva nombre y colita.
                const primeroDeRacha = nuevoDia || !anterior || anterior.autor_id !== m.autor_id;
                const separador = nuevoDia ? (
                  <div className="flex justify-center pt-3 pb-1 first:pt-0" data-testid="chat-dia">
                    <span className={PASTILLA}>{etiquetaDia(m.creado_at)}</span>
                  </div>
                ) : null;
                diaAnterior = dia;
                anterior = m;
                return (
                  <React.Fragment key={m.id}>
                    {separador}
                    <BurbujaChat m={m} sala={salaId} mio={m.autor_id === yo}
                      primeroDeRacha={primeroDeRacha} puedeBorrar={puedeBorrar} onBorrar={borrar}
                      onAbrirFoto={(msg, inicio) => setVisor({ id: msg.id, inicio })}
                      transcripcion={transcripcion} onTranscrita={alTranscribir} />
                  </React.Fragment>
                );
              })}
            </>
          )}
        </div>

        {/* La caja, como en WhatsApp: el clip a la izquierda, el texto en una
            cápsula y el botón de enviar, redondo y verde, a la derecha. */}
        <div className="border-t border-black/5 dark:border-white/5 bg-[#F0F2F5] dark:bg-[#202C33] px-2 sm:px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {errorNota && !nota && (
            <div className="mb-2 text-sm text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 rounded-lg px-3 py-2 flex items-start gap-2"
              data-testid="chat-nota-error" role="alert">
              <div className="flex-1">{errorNota}</div>
              <button type="button" onClick={() => setErrorNota('')}
                className="shrink-0 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40"
                aria-label="Cerrar el aviso">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {nota?.aviso && nota.fase === 'lista' && !nota.error && (
            <div className="mb-2 text-sm text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 rounded-lg px-3 py-2"
              data-testid="chat-nota-aviso" role="status">
              {nota.aviso}
            </div>
          )}
          {nota?.error && (
            <div className="mb-2 text-sm text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2"
              data-testid="chat-nota-fallo-envio" role="alert">
              <span className="flex-1 min-w-[12rem]">
                {nota.archivo
                  ? `No se ha enviado el audio: ${nota.error}. El audio sigue aquí: puedes oírlo y volver a enviarlo.`
                  : `No se ha enviado la nota de voz: ${nota.error}. La nota sigue aquí: puedes oírla y volver a enviarla.`}
              </span>
              <Button variant="outline" size="sm" onClick={enviarNota} disabled={nota.fase !== 'lista'}
                data-testid="chat-nota-reintentar">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reintentar
              </Button>
            </div>
          )}
          {avisosSala.length > 0 && (
            <div className="mb-2 text-sm text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 rounded-lg px-3 py-2 flex items-start gap-2"
              data-testid="chat-fotos-aviso" role="alert">
              <div className="flex-1 space-y-0.5">
                {avisosSala.map((a) => <div key={a}>{a}</div>)}
              </div>
              <button type="button" onClick={() => setAvisosFotos((prev) => ({ ...prev, [salaId]: [] }))}
                className="shrink-0 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40"
                aria-label="Cerrar el aviso">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {falloEnvio && (
            <div className="mb-2 text-sm text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2"
              data-testid="chat-envio-fallo" role="alert">
              <span className="flex-1 min-w-[12rem]">
                No se ha enviado: {falloEnvio}. {adjuntosSala.length
                  ? `Tu mensaje y tus ${pdfsSala.length ? 'archivos' : 'fotos'} siguen aquí.`
                  : 'Tu mensaje sigue en la caja.'}
              </span>
              <Button variant="outline" size="sm" onClick={enviar} disabled={Boolean(enviando)}
                data-testid="chat-reintentar-envio">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reintentar
              </Button>
            </div>
          )}
          {adjuntosSala.length > 0 && (
            <div className="mb-2 flex gap-2 overflow-x-auto pb-0.5" data-testid="chat-adjuntos">
              {fotosSala.map((f) => (
                <div key={f.clave}
                  className="relative shrink-0 w-[72px] h-[72px] rounded-lg overflow-hidden bg-black/10 dark:bg-white/10 flex items-center justify-center"
                  data-testid="chat-adjunto" title={f.archivo.name}>
                  {/* Detrás, el nombre: lo que se ve si el navegador no sabe
                      pintar la vista previa (un HEIC fuera de Safari). */}
                  <span className="absolute inset-1 text-[9px] leading-tight text-[#54656F] dark:text-[#8696A0] break-all overflow-hidden">
                    {f.archivo.name}
                  </span>
                  {f.url && (
                    <img src={f.url} alt={f.archivo.name} className="relative w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  )}
                  <button type="button" onClick={() => quitarFoto(f.clave)} disabled={enviando === salaId}
                    className="absolute top-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/60 text-white hover:bg-black/80 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    aria-label={`Quitar ${f.archivo.name || 'la foto'}`} data-testid="chat-adjunto-quitar">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {/* Los PDF (29/9/2026): una ficha con el nombre y el tamaño. */}
              {pdfsSala.map((f) => (
                <div key={f.clave}
                  className="relative shrink-0 w-[200px] h-[72px] rounded-lg bg-white dark:bg-[#2A3942] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] flex items-center gap-2 pl-2 pr-8"
                  data-testid="chat-adjunto-pdf" title={f.archivo.name}>
                  <span className="shrink-0 inline-flex items-center justify-center w-9 h-11 rounded bg-[#E5484D] text-white" aria-hidden="true">
                    <FileText className="w-5 h-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[12.5px] font-medium text-[#111B21] dark:text-[#E9EDEF]">
                      {sinDireccion(f.archivo.name) || 'documento.pdf'}
                    </span>
                    <span className="block text-[11px] text-[#667781] dark:text-[#8696A0]">
                      PDF · {tamanoLegible(f.archivo.size)}
                    </span>
                  </span>
                  <button type="button" onClick={() => quitarFoto(f.clave)} disabled={enviando === salaId}
                    className="absolute top-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/60 text-white hover:bg-black/80 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    aria-label={`Quitar ${f.archivo.name || 'el PDF'}`} data-testid="chat-adjunto-quitar">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {progreso != null && enviando === salaId && (
            <div className="mb-2" data-testid="chat-progreso">
              <div className="h-1.5 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden"
                role="progressbar" aria-label={`Subiendo ${loQueSeSube(adjuntosSala)}`}
                aria-valuemin={0} aria-valuemax={100} aria-valuenow={progreso}>
                <div className="h-full bg-[#00A884] transition-[width] duration-200" style={{ width: `${progreso}%` }} />
              </div>
              <div className="mt-0.5 text-[11px] text-[#54656F] dark:text-[#8696A0] tabular-nums">
                Subiendo {loQueSeSube(adjuntosSala)}… {progreso} %
              </div>
            </div>
          )}
          {nota ? (
            // La nota de voz, en el sitio de la caja: descartar a la
            // izquierda, el tiempo (o, ya grabada, su reproductor) en medio
            // y enviar a la derecha, donde siempre.
            <div className="flex items-end gap-2">
              <div className="flex-1 min-w-0 flex items-center gap-2 rounded-3xl bg-[#FFFFFF] dark:bg-[#2A3942] pl-1 pr-3 py-1 min-h-[3rem] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]"
                data-testid="chat-nota-grabando" data-fase={nota.fase}>
                <button type="button" onClick={descartarNota} disabled={nota.fase === 'enviando'}
                  className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                  title={nota.archivo ? 'Quitar el audio' : 'Descartar la nota de voz'}
                  aria-label={nota.archivo ? 'Quitar el audio' : 'Descartar la nota de voz'} data-testid="chat-nota-descartar">
                  <Trash2 className="w-5 h-5" />
                </button>
                {['lista', 'enviando'].includes(nota.fase) ? (
                  <div className="flex-1 min-w-0 py-1">
                    {nota.archivo && (
                      // El audio elegido (29/9/2026): su nombre, encima.
                      <div className="flex items-center gap-1 pb-0.5 text-[12.5px] font-medium text-[#111B21] dark:text-[#E9EDEF] min-w-0"
                        data-testid="chat-nota-archivo">
                        <Music className="w-3.5 h-3.5 shrink-0 text-[#667781] dark:text-[#8696A0]" aria-hidden="true" />
                        <span className="truncate" title={nota.nombre}>{sinDireccion(nota.nombre)}</span>
                      </div>
                    )}
                    <ReproductorNota src={nota.url} duracionS={nota.duracionMs / 1000} testid="chat-nota-previa"
                      icono={nota.archivo ? Music : undefined}
                      textoFallo={nota.archivo
                        ? 'La vista previa no se puede oír en este navegador. El audio se puede enviar igual.'
                        : 'La vista previa no se puede oír en este navegador. La nota se puede enviar igual.'} />
                  </div>
                ) : (
                  <div className="flex-1 min-w-0 flex items-center gap-2" role="status" aria-live="polite">
                    {nota.fase === 'grabando'
                      ? <span className="shrink-0 inline-block w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" aria-hidden="true" />
                      : <Loader2 className="shrink-0 w-4 h-4 animate-spin text-[#54656F] dark:text-[#8696A0]" aria-hidden="true" />}
                    <span className="tabular-nums font-medium text-[15px] text-[#111B21] dark:text-[#E9EDEF]" data-testid="chat-nota-tiempo">
                      {duracionCorta(nota.segundos)}
                    </span>
                    <span className="min-w-0 truncate text-[12.5px] text-[#667781] dark:text-[#8696A0]">
                      {nota.fase === 'pidiendo' && 'Pidiendo permiso para el micrófono…'}
                      {nota.fase === 'grabando' && `Grabando · máximo ${duracionCorta(TOPE_SEGUNDOS_NOTA)}`}
                      {nota.fase === 'parando' && 'Preparando la nota de voz…'}
                    </span>
                  </div>
                )}
              </div>
              <button type="button" onClick={enviarNota} disabled={!['grabando', 'lista'].includes(nota.fase)}
                className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white shadow-sm disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#2E6E5E]"
                aria-label={nota.archivo ? 'Enviar el audio' : 'Enviar la nota de voz'} data-testid="chat-nota-enviar">
                {['pidiendo', 'parando', 'enviando'].includes(nota.fase)
                  ? <Loader2 className="w-5 h-5 animate-spin" /> : <SendHorizontal className="w-5 h-5" />}
              </button>
            </div>
          ) : (
          <div className="flex items-end gap-2">
            <div className="flex-1 min-w-0 flex items-end gap-0.5 rounded-3xl bg-[#FFFFFF] dark:bg-[#2A3942] pl-1 pr-1 py-1 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
              {/* El clip: los archivos o la galería. Tipos CONCRETOS y sin
                  `capture` (dirección: «pero no sacar foto con el móvil»).
                  Desde el 29/9/2026, también PDF y audios. */}
              <button type="button" onClick={() => entradaFotosRef.current?.click()}
                disabled={enviando === salaId}
                className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full text-[#54656F] dark:text-[#8696A0] hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A884]"
                title="Adjuntar fotos, PDF o un audio" aria-label="Adjuntar fotos, PDF o un audio" data-testid="chat-adjuntar">
                <Paperclip className="w-5 h-5 -rotate-45" />
              </button>
              <input ref={entradaFotosRef} type="file" accept={ACEPTA_ADJUNTOS} multiple className="hidden"
                onChange={alElegirFotos} tabIndex={-1} aria-hidden="true" data-testid="chat-adjuntar-input" />
              {/* 16 px en el móvil (text-base): con menos, Safari del iPhone
                  hace zoom al tocar la caja. En el ordenador, 15 px. */}
              <textarea
                ref={cajaRef}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={alTeclear}
                onPaste={alPegar}
                readOnly={enviando === salaId}
                rows={1}
                placeholder={adjuntosSala.length ? 'Añade un texto (opcional)…' : 'Escribe un mensaje…'}
                aria-label={`Mensaje para la sala ${sala?.nombre || ''}`}
                className="flex-1 min-w-0 resize-none bg-transparent border-0 outline-none focus:ring-0 py-2.5 max-h-40 text-base md:text-[15px] leading-5 text-[#111B21] dark:text-[#E9EDEF] placeholder:text-[#667781] dark:placeholder:text-[#8696A0]"
                data-testid="chat-texto"
              />
              {/* El micrófono: una nota de voz (28/9/2026). No con fotos o
                  PDF elegidos: la nota va sola, y se quedarían esperando. */}
              <button type="button" onClick={empezarNota}
                disabled={enviando === salaId || adjuntosSala.length > 0}
                className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full text-[#54656F] dark:text-[#8696A0] hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A884]"
                title={adjuntosSala.length
                  ? `Envía o quita ${pdfsSala.length ? 'las fotos y los PDF' : 'las fotos'} antes de grabar una nota de voz`
                  : 'Grabar una nota de voz'}
                aria-label="Grabar una nota de voz" data-testid="chat-grabar">
                <Mic className="w-5 h-5" />
              </button>
            </div>
            <button type="button" onClick={enviar} disabled={!puedeEnviar}
              className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white shadow-sm disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#2E6E5E]"
              aria-label="Enviar" data-testid="chat-enviar">
              {enviando === salaId ? <Loader2 className="w-5 h-5 animate-spin" /> : <SendHorizontal className="w-5 h-5" />}
            </button>
          </div>
          )}
          {/* La pista del teclado, solo donde hay teclado físico: en el móvil
              no cabía y allí no hay Mayúsculas+Enter. */}
          <div className="hidden sm:block text-[11px] text-[#667781] dark:text-[#8696A0] mt-1 pl-2">
            Enter envía · Mayúsculas+Enter, otra línea · arrastra o pega fotos, PDF o un audio aquí · el micrófono graba una nota de voz
          </div>
          {largo(texto) > TOPE_TEXTO - 500 && (
            <div className={`text-[11px] mt-1 text-right tabular-nums ${largo(texto.trim()) > TOPE_TEXTO ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground'}`}
              data-testid="chat-cuenta">
              {largo(texto.trim())} / {TOPE_TEXTO}
            </div>
          )}
        </div>
        {mensajeVisor && (
          <VisorFotos key={mensajeVisor.id} sala={salaId} fotos={mensajeVisor.fotos}
            inicio={visor.inicio} autor={sinDireccion(mensajeVisor.autor_nombre)}
            onCerrar={() => setVisor(null)} />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-140px)] min-h-[420px]" data-testid="chat-page">
      <PageHeader title="Chat" subtitle="Habla con el equipo. Los mensajes se quedan en el historial." />
      {/* Arrastrar fotos, PDF o un audio sobre cualquier parte del chat
          los adjunta. */}
      <div className="cjo-card relative flex-1 min-h-0 flex flex-col overflow-hidden -mt-2"
        onDragEnter={salaId ? alEntrarArrastrando : undefined}
        onDragOver={salaId ? alArrastrarEncima : undefined}
        onDragLeave={salaId ? alSalirArrastrando : undefined}
        onDrop={salaId ? alSoltar : undefined}
        data-testid="chat-zona">
        {cuerpo}
        {arrastrando && (
          <div className="absolute inset-2 z-10 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#00A884] bg-[#F0F2F5]/90 dark:bg-[#0B141A]/90 text-[#111B21] dark:text-[#E9EDEF] pointer-events-none"
            data-testid="chat-soltar">
            <Paperclip className="w-10 h-10 text-[#00A884] -rotate-45" />
            {enviando && enviando === salaId ? (
              <div className="text-base font-medium text-center px-4">Espera a que terminen de subirse {loQueSeSube(adjuntosSala)}</div>
            ) : (
              <>
                <div className="text-base font-medium text-center px-4">Suelta aquí las fotos, los PDF o un audio</div>
                <div className="text-xs text-[#667781] dark:text-[#8696A0] text-center px-4">
                  Fotos (JPG, PNG, WEBP o HEIC) y PDF: hasta {TOPE_FOTOS} de cada, de {TOPE_MB_PDF} MB como mucho
                </div>
                <div className="text-xs text-[#667781] dark:text-[#8696A0] text-center px-4">
                  Un audio ({FORMATOS_AUDIO}): de {TOPE_MB_AUDIO} MB y {TOPE_SEGUNDOS_AUDIO / 60} minutos como mucho
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
